import { useCallback, useEffect, useState } from "react";
import { Loader2, Lock, LockOpen } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";
import {
  TAX_SUMMARY_MONTH_KEYS,
  TAX_SUMMARY_MONTH_LABELS,
  isTaxSummaryMonthClosed,
  taxSummaryMonthClosureLabel,
} from "@/lib/taxSummaryWorkbookConstants";

function formatHistoryWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX");
  } catch {
    return iso;
  }
}

function actorDisplayName(user) {
  return user?.name || user?.email || "Usuario";
}

export default function TaxSummaryMonthClosurePanel({
  fiscalClientId,
  year,
  readOnly = false,
  canReopen = false,
  currentUser = null,
  workbookMeta,
  onWorkbookItem,
}) {
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState("");
  const [closures, setClosures] = useState({});
  const [history, setHistory] = useState([]);
  const [closeMonth, setCloseMonth] = useState("");
  const [closeObservation, setCloseObservation] = useState("");
  const [reopenMonth, setReopenMonth] = useState("");
  const [reopenPassword, setReopenPassword] = useState("");
  const [reopenReason, setReopenReason] = useState("");

  const loadClosures = useCallback(async () => {
    if (!fiscalClientId || !year) return;
    setLoading(true);
    try {
      const data = await despachoFiscalInfoApi.getTaxSummaryMonthClosures({
        fiscalClientId,
        year,
      });
      setClosures(data?.month_closures || {});
      setHistory(Array.isArray(data?.history) ? data.history : []);
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setLoading(false);
    }
  }, [fiscalClientId, year]);

  useEffect(() => {
    loadClosures();
  }, [loadClosures, workbookMeta]);

  const applyItem = (item) => {
    if (item?.workbook?.meta?.month_closures) {
      setClosures(item.workbook.meta.month_closures);
    }
    onWorkbookItem?.(item);
    loadClosures();
  };

  const resetCloseDialog = () => {
    setCloseMonth("");
    setCloseObservation("");
  };

  const resetReopenDialog = () => {
    setReopenMonth("");
    setReopenPassword("");
    setReopenReason("");
  };

  const handleClose = async () => {
    if (readOnly || !closeMonth) return;
    const obs = closeObservation.trim();
    if (!obs) {
      toast.error("Indique una observación al cerrar el periodo.");
      return;
    }
    const monthKey = closeMonth;
    setActing(`close-${monthKey}`);
    try {
      const data = await despachoFiscalInfoApi.closeTaxSummaryMonth({
        fiscalClientId,
        year,
        monthKey,
        observation: obs,
      });
      resetCloseDialog();
      applyItem(data?.item);
      toast.success(`Periodo ${TAX_SUMMARY_MONTH_LABELS[monthKey]} cerrado.`);
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setActing("");
    }
  };

  const handleReopen = async () => {
    if (!reopenMonth || readOnly || !canReopen) return;
    const reason = reopenReason.trim();
    if (!reason) {
      toast.error("Indique el motivo de la reapertura.");
      return;
    }
    if (!reopenPassword.trim()) {
      toast.error("Ingrese su contraseña de administrador para reabrir.");
      return;
    }
    const monthKey = reopenMonth;
    setActing(`reopen-${monthKey}`);
    try {
      const data = await despachoFiscalInfoApi.reopenTaxSummaryMonth({
        fiscalClientId,
        year,
        monthKey,
        observation: reason,
        password: reopenPassword,
      });
      resetReopenDialog();
      applyItem(data?.item);
      toast.success(`Periodo ${TAX_SUMMARY_MONTH_LABELS[monthKey]} reabierto.`);
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setActing("");
    }
  };

  if (loading) {
    return (
      <p className="text-xs text-slate-500 flex items-center gap-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        Cargando cierres de periodo…
      </p>
    );
  }

  return (
    <div className="space-y-4" data-testid="tax-summary-month-closure-panel">
      <p className="text-xs text-slate-600 m-0">
        Al cerrar un mes se solicita observación y queda registrado quién lo cerró. Los meses
        cerrados no admiten edición ni recálculo. La reapertura requiere contraseña del
        administrador y motivo.
      </p>

      <div className="overflow-auto rounded-sm border border-slate-200">
        <table className="w-full border-collapse text-[11px]">
          <thead className="bg-slate-50">
            <tr>
              <th className="border-b border-r border-slate-200 p-2 text-left">Mes</th>
              <th className="border-b border-r border-slate-200 p-2 text-left">Estatus</th>
              <th className="border-b border-r border-slate-200 p-2 text-left">Cerrado / reabierto por</th>
              <th className="border-b border-r border-slate-200 p-2 text-left">Última observación</th>
              <th className="border-b border-slate-200 p-2 text-left">Acción</th>
            </tr>
          </thead>
          <tbody>
            {TAX_SUMMARY_MONTH_KEYS.map((monthKey) => {
              const closed = isTaxSummaryMonthClosed({ month_closures: closures }, monthKey);
              const entry = closures[monthKey] || {};
              const busy = acting === `close-${monthKey}` || acting === `reopen-${monthKey}`;
              return (
                <tr key={monthKey} className="border-b border-slate-100">
                  <td className="border-r border-slate-100 p-2 font-medium">
                    {TAX_SUMMARY_MONTH_LABELS[monthKey]}
                  </td>
                  <td className="border-r border-slate-100 p-2">
                    <span
                      className={
                        closed
                          ? "inline-flex items-center gap-1 text-slate-700"
                          : "inline-flex items-center gap-1 text-emerald-700"
                      }
                    >
                      {closed ? <Lock className="h-3 w-3" aria-hidden /> : <LockOpen className="h-3 w-3" aria-hidden />}
                      {taxSummaryMonthClosureLabel({ month_closures: closures }, monthKey)}
                    </span>
                  </td>
                  <td className="border-r border-slate-100 p-2 text-slate-600">
                    {entry.updated_by_name || "—"}
                  </td>
                  <td className="border-r border-slate-100 p-2 text-slate-600">
                    {entry.observation || "—"}
                  </td>
                  <td className="p-2">
                    {!readOnly && !closed ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-[10px]"
                        disabled={Boolean(acting)}
                        onClick={() => {
                          setCloseObservation("");
                          setCloseMonth(monthKey);
                        }}
                      >
                        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Cerrar mes…"}
                      </Button>
                    ) : null}
                    {!readOnly && closed && canReopen ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-[10px]"
                        disabled={Boolean(acting)}
                        onClick={() => {
                          setReopenPassword("");
                          setReopenReason("");
                          setReopenMonth(monthKey);
                        }}
                      >
                        Reabrir…
                      </Button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={Boolean(closeMonth)} onOpenChange={(open) => { if (!open) resetCloseDialog(); }}>
        <DialogContent className="sm:max-w-md" data-testid="tax-summary-close-month-dialog">
          <DialogHeader>
            <DialogTitle>
              Cerrar {closeMonth ? TAX_SUMMARY_MONTH_LABELS[closeMonth] : "mes"}
            </DialogTitle>
            <DialogDescription>
              El periodo quedará bloqueado para edición y recálculo hasta reabrirlo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Cerrado por</Label>
              <p className="text-sm font-medium text-slate-800 m-0">{actorDisplayName(currentUser)}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="tax-summary-close-observation">
                Observación
              </Label>
              <Textarea
                id="tax-summary-close-observation"
                value={closeObservation}
                onChange={(e) => setCloseObservation(e.target.value)}
                rows={3}
                className="text-xs"
                placeholder="Ej. Revisado con auxiliar, listo para declaración provisional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={resetCloseDialog}>
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={acting.startsWith("close")}
              onClick={handleClose}
            >
              Confirmar cierre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(reopenMonth)} onOpenChange={(open) => { if (!open) resetReopenDialog(); }}>
        <DialogContent className="sm:max-w-md" data-testid="tax-summary-reopen-month-dialog">
          <DialogHeader>
            <DialogTitle>
              Reabrir {reopenMonth ? TAX_SUMMARY_MONTH_LABELS[reopenMonth] : "mes"}
            </DialogTitle>
            <DialogDescription>
              Se habilitará de nuevo la edición del mes. Requiere su contraseña de administrador.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Reabierto por</Label>
              <p className="text-sm font-medium text-slate-800 m-0">{actorDisplayName(currentUser)}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="tax-summary-reopen-reason">Motivo de reapertura</Label>
              <Textarea
                id="tax-summary-reopen-reason"
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                rows={3}
                className="text-xs"
                placeholder="Ej. Corrección de acreditamiento ISR"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="tax-summary-reopen-password">
                Contraseña del administrador
              </Label>
              <Input
                id="tax-summary-reopen-password"
                type="password"
                value={reopenPassword}
                onChange={(e) => setReopenPassword(e.target.value)}
                className="h-8 text-xs"
                autoComplete="current-password"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleReopen();
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={resetReopenDialog}>
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={acting.startsWith("reopen")}
              onClick={handleReopen}
            >
              Confirmar reapertura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {history.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-700 m-0">Historial reciente</p>
          <ul className="text-[10px] text-slate-600 space-y-1 m-0 pl-4 list-disc max-h-40 overflow-auto">
            {history.slice(0, 20).map((row) => (
              <li key={row.id || `${row.month_key}-${row.created_at}`}>
                {formatHistoryWhen(row.created_at)} — {row.month_key}: {row.action}
                {row.actor_user_name ? ` · ${row.actor_user_name}` : ""}
                {row.observation ? ` · ${row.observation}` : ""}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
